// Pure confirmation layer for the queue DELIVERY alerts (#275).
//
// #240 closed with production proof that Kalshi's bulk queue-position response
// itself oscillates for an unchanged resting order: GBuff recorded 4,033 fresh
// same-ID increases, fresh-to-fresh, same-remaining, same-credential. So a
// single favourable sample is not evidence of a real FIFO advance — an order
// can read ~700,000 behind, then ~9,000, then ~700,000 again. The shipped edge
// detector treats that first low sample as a crossing and immediately rings.
//
// This layer holds a downward crossing for ONE more eligible sample before
// delivering it. It reports events only: it applies no preference, plays
// nothing, renders nothing, and — like stepQueueDelivery — never modifies a
// queue value. `nextQueue` / `nextAbove` pass through exactly as measured, so
// the raw number stays visible to the trader the instant it arrives. Only the
// ALARM waits.
//
// Rules:
//  - A tier's downward crossing becomes a pending CANDIDATE for that order
//    instead of firing.
//  - The candidate CONFIRMS when the next eligible numeric sample for that
//    order is still within the crossed bound; the ordinary crossing is then
//    emitted exactly once, at confirmation.
//  - It is DISCARDED, silently, if that sample rebounds above the bound, is
//    null, or the order is gone. The next numeric observation seeds normally
//    through stepQueueDelivery's own first-sight rule.
//  - The two tiers are tracked SEPARATELY. A partial rebound (past critical,
//    still inside warning) confirms warning and drops critical — each tier is
//    delivered independently downstream, so each is confirmed independently
//    here. One confirmed move can still cross both, which is what preserves
//    the shipped both-tiers-at-once behaviour.
//  - fellBehind is untouched. It is the UNFAVOURABLE direction — an order
//    slipping behind is not the failure mode #240 documented, and #275 leaves
//    "queueAbove/fell-behind behavior unchanged" explicitly.
//
// Cost is O(orders) per tick with two small Sets — this runs on the same 4 Hz
// market clock as queueMetricsByOrderId, so nothing here may scan per order.

import {
  stepQueueDelivery,
  orderedQueueBounds,
  type QueueDeliveryStepResult,
  type QueueDeliveryThresholds,
} from './queueDelivery';

/**
 * Orders whose latest crossing is awaiting a second, agreeing sample, each
 * stamped with the SOURCE SAMPLE it was raised on.
 *
 * The stamp is what makes "the next sample" mean the next sample. This step
 * runs on the 4 Hz market clock, which re-reads the same Kalshi queue reading
 * many times between polls — without the stamp, a candidate confirms ~250 ms
 * later against a re-read of the very reading it was doubting, which is no
 * evidence at all and reduces the whole layer to a brief pause.
 */
export interface QueueCrossingConfirmations {
  readonly warning: ReadonlyMap<string, unknown>;
  readonly critical: ReadonlyMap<string, unknown>;
}

/** Seed value for the first tick (page load, reconnect, remount). */
export const NO_QUEUE_CONFIRMATIONS: QueueCrossingConfirmations = {
  warning: new Map<string, unknown>(),
  critical: new Map<string, unknown>(),
};

interface QueueMetric {
  queue: number | null;
  queueAbove: number | null;
}

export interface ConfirmedQueueDeliveryResult {
  /** Same shape stepQueueDelivery returns, so `selectQueueSoundChannel` and
   *  every flash gate consume it unchanged — warning/critical now carry only
   *  CONFIRMED crossings. */
  events: QueueDeliveryStepResult;
  /** Candidates carried into the next tick. Also the set of orders whose
   *  current sample is an unconfirmed favourable outlier. */
  nextPending: QueueCrossingConfirmations;
}

/**
 * Resolve one tier: confirm or discard each carried candidate against this
 * tick's sample, then take this tick's fresh crossings as the new candidates.
 *
 * A candidate can never also be a fresh crossing for the SAME tier in one tick
 * — being a candidate means the previous sample was already within the bound,
 * so `last > bound` cannot hold — which is why the two loops cannot collide.
 */
function stepTier(
  pending: ReadonlyMap<string, unknown>,
  crossedThisTick: readonly string[],
  metricsByOrderId: ReadonlyMap<string, QueueMetric>,
  bound: number,
  sampleToken: unknown
): { confirmed: string[]; nextPending: Map<string, unknown> } {
  const confirmed: string[] = [];
  const nextPending = new Map<string, unknown>();
  for (const [orderId, raisedAt] of pending) {
    if (raisedAt === sampleToken) {
      // Still the same source reading. Not a second opinion — hold, silently.
      nextPending.set(orderId, raisedAt);
      continue;
    }
    const q = metricsByOrderId.get(orderId)?.queue;
    // Absent order, or a null reading: discard without delivery. Both are
    // "no eligible sample", and stepQueueDelivery has already made the next
    // numeric reading a silent first sight.
    if (q == null) continue;
    if (q <= bound) confirmed.push(orderId);
    // else: rebounded above the bound — the low sample was not real.
  }
  // Raised (or re-raised) on the reading currently in hand.
  for (const orderId of crossedThisTick) nextPending.set(orderId, sampleToken);
  return { confirmed, nextPending };
}

/**
 * One tick of queue delivery, with favourable crossings confirmed.
 *
 * This is the composed entry point PositionTracker calls — the raw step and the
 * confirmation are wired together HERE, once, so the view holds carry-over
 * state and nothing else. Tests drive this same function.
 */
/**
 * `sampleToken` identifies the SOURCE READING this call is looking at — pass
 * something whose identity changes once per physical queue-position read and
 * not on every render or push envelope. PositionTracker uses the relay's
 * queue `fetchedAt`, with a distinct legacy-sequence namespace when older
 * relays do not expose source identity.
 *
 * It is compared by identity and never dereferenced, so any stable value works.
 * Passing a fresh value every call silently reduces this layer to "wait one
 * render", which on a 4 Hz clock is no wait at all.
 */
export function stepConfirmedQueueDelivery(
  prevQueue: ReadonlyMap<string, number>,
  prevAbove: ReadonlyMap<string, number>,
  pending: QueueCrossingConfirmations,
  metricsByOrderId: ReadonlyMap<string, QueueMetric>,
  thresholds: QueueDeliveryThresholds,
  sampleToken: unknown
): ConfirmedQueueDeliveryResult {
  const raw = stepQueueDelivery(prevQueue, prevAbove, metricsByOrderId, thresholds);
  const { criticalAt, warningAt } = orderedQueueBounds(thresholds);

  const critical = stepTier(
    pending.critical,
    raw.criticalOrderIds,
    metricsByOrderId,
    criticalAt,
    sampleToken
  );
  const warning = stepTier(
    pending.warning,
    raw.warningOrderIds,
    metricsByOrderId,
    warningAt,
    sampleToken
  );

  return {
    events: {
      // Measurements pass through untouched — this layer gates delivery only.
      nextQueue: raw.nextQueue,
      nextAbove: raw.nextAbove,
      warningOrderIds: warning.confirmed,
      criticalOrderIds: critical.confirmed,
      fellBehindOrderIds: raw.fellBehindOrderIds,
    },
    nextPending: { warning: warning.nextPending, critical: critical.nextPending },
  };
}
